import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, LogOut } from 'lucide-react';
import useHandleLogout from 'src/hooks/useHandleLogout';
import { selectUserState } from 'src/lib/features/auth/authSlice';
import { useTypedSelector } from 'src/lib/store/store';
import ReferralModal from '../Modal/ReferralModal';
import MenuItems from './MenuItems';
import { SocialLinks } from './SideNav';
import { Avatar } from './UserInfo';
import { useNavGroups } from './navItems';

// See the note in SideNav.jsx: served from `public/`, not imported.
const LogoLockup = `${process.env.PUBLIC_URL}/assets/droomlogo.svg`;

/**
 * Mobile account drawer. Primary navigation lives in the bottom tab bar, so
 * this carries identity, the secondary nav group, and account actions.
 */
const MobileMenu = ({ showMobileMenu, setShowMobileMenu }) => {
  const [showReferralModal, setShowReferralModal] = useState(false);
  const { handleLogout } = useHandleLogout();
  const { primary, secondary } = useNavGroups();
  const user = useTypedSelector(selectUserState).user;

  const close = () => setShowMobileMenu && setShowMobileMenu(false);

  if (!showMobileMenu) return null;

  return (
    <div className='fixed inset-0 z-50 lg:hidden'>
      <button
        type='button'
        aria-label='Close menu'
        onClick={close}
        className='absolute inset-0 h-full w-full bg-brand-navy/40'
      />

      <div className='relative flex h-full w-full max-w-sm flex-col bg-card shadow-ui-lg'>
        <div className='flex h-14 shrink-0 items-center justify-between border-b px-4'>
          {/* Same lockup as the rail, so the drawer and the desktop shell
              show the same brand rather than artwork in one and type in
              the other. */}
          <img
            src={LogoLockup}
            alt='Droomwork'
            width={255}
            height={70}
            className='h-8 w-auto'
          />
          <button
            type='button'
            onClick={close}
            aria-label='Close menu'
            className='-mr-2 rounded-full p-2 text-brand-navy hover:bg-surface'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='flex-1 space-y-4 overflow-y-auto p-3'>
          <Link
            to='/profile'
            onClick={close}
            className='flex items-center gap-3 rounded-xl border bg-surface p-3'
          >
            <Avatar
              photo={user?.photo}
              first={user?.firstName}
              last={user?.lastName}
              className='h-11 w-11'
            />
            <span className='min-w-0'>
              <span className='block truncate text-sm font-semibold capitalize text-brand-navy'>
                {[user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
                  'My profile'}
              </span>
              <span className='block truncate text-xs text-muted-foreground'>
                {user?.email}
              </span>
            </span>
          </Link>

          <MenuItems label='My Employment' items={primary} onNavigate={close} />

          <MenuItems
            label='Explore on Droomwork'
            items={secondary}
            muted
            onNavigate={close}
          />

          <div>
            <div className='px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
              Account
            </div>
            <div className='space-y-1'>
              {/* Refer & Earn hidden for now; restore by uncommenting and re-adding Gift to the lucide import. */}
              {/* <button type='button' onClick={() => setShowReferralModal(true)} className='flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-brand-navy transition-colors hover:bg-surface' > <Gift className='h-4 w-4 shrink-0' /> Refer &amp; Earn </button> */}
              <button
                type='button'
                onClick={() => handleLogout()}
                className='flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface'
              >
                <LogOut className='h-4 w-4 shrink-0' />
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className='shrink-0 border-t px-6 py-4'>
          <div className='pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
            Follow us
          </div>
          <SocialLinks />
        </div>
      </div>

      <ReferralModal
        open={showReferralModal}
        closeModal={() => setShowReferralModal(false)}
      />
    </div>
  );
};

export default MobileMenu;
